import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Location, Screen, ScreenGroup } from '@ghostscreen/contracts';
import { ConfirmDialog } from '../../components/dashboard/ConfirmDialog';
import { apiErrorMessage } from '../../i18n';
import { api } from '../../lib/api';
import { getCardConnectionStatus } from '../../lib/dashboard/screenStatus';
import { useTheme } from '../../theme/ThemeProvider';
import {
  buildGroupPresets,
  groupScreensByLocation,
  statusLabel,
  type GroupScreenPreset,
} from './buildGroupCardModel';

type GroupDrawerProps = {
  open: boolean;
  organisationId: string;
  /** null = create mode */
  group: ScreenGroup | null;
  screens: Screen[];
  locations: Location[];
  onClose: () => void;
};

/**
 * New / edit group drawer — Ghost Screen Groups.dc.html.
 * Create commits on submit; edit saves name/members as you go.
 */
export function GroupDrawer({
  open,
  organisationId,
  group,
  screens,
  locations,
  onClose,
}: GroupDrawerProps) {
  const { t } = useTranslation(['groups', 'common']);
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const editing = Boolean(group);
  const [name, setName] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['screen-group', organisationId, group?.id],
    queryFn: () => api.getScreenGroup(organisationId, group!.id),
    enabled: open && Boolean(group?.id),
  });

  useEffect(() => {
    if (!open) return;
    setError(null);
    setConfirmDelete(false);
    if (!group) {
      setName('');
      setSelectedIds([]);
      return;
    }
    setName(group.name);
  }, [open, group]);

  useEffect(() => {
    if (!open || !group || !detailQuery.data) return;
    setSelectedIds(detailQuery.data.screenIds);
  }, [open, group, detailQuery.data]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['screen-groups', organisationId] }),
      group
        ? queryClient.invalidateQueries({
            queryKey: ['screen-group', organisationId, group.id],
          })
        : Promise.resolve(),
      queryClient.invalidateQueries({ queryKey: ['schedules', organisationId] }),
    ]);
  };

  const create = useMutation({
    mutationFn: async () => {
      const created = await api.createScreenGroup(organisationId, { name: name.trim() });
      for (const screenId of selectedIds) {
        await api.addScreenGroupMember(organisationId, created.id, { screenId });
      }
      return created;
    },
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, t('createError'))),
  });

  const rename = useMutation({
    mutationFn: (next: string) =>
      api.updateScreenGroup(organisationId, group!.id, { name: next }),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, t('drawer.renameError'))),
  });

  const addMember = useMutation({
    mutationFn: (screenId: string) =>
      api.addScreenGroupMember(organisationId, group!.id, { screenId }),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, t('drawer.addError'))),
  });

  const removeMember = useMutation({
    mutationFn: (screenId: string) =>
      api.removeScreenGroupMember(organisationId, group!.id, screenId),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, t('drawer.removeError'))),
  });

  const removeGroup = useMutation({
    mutationFn: () => api.deleteScreenGroup(organisationId, group!.id),
    onSuccess: async () => {
      setConfirmDelete(false);
      await invalidate();
      onClose();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, t('drawer.deleteError'))),
  });

  const sections = useMemo(
    () => groupScreensByLocation(screens, locations),
    [screens, locations],
  );
  const presets = useMemo(
    () => buildGroupPresets({ screens, locations }),
    [screens, locations],
  );

  const busy =
    create.isPending ||
    rename.isPending ||
    addMember.isPending ||
    removeMember.isPending ||
    removeGroup.isPending;

  const canCreate = Boolean(name.trim()) && selectedIds.length > 0 && !busy;

  const toggleScreen = (screenId: string) => {
    const on = selectedIds.includes(screenId);
    if (!editing) {
      setSelectedIds((ids) =>
        on ? ids.filter((id) => id !== screenId) : [...ids, screenId],
      );
      return;
    }
    if (on) {
      setSelectedIds((ids) => ids.filter((id) => id !== screenId));
      removeMember.mutate(screenId);
    } else {
      setSelectedIds((ids) => [...ids, screenId]);
      addMember.mutate(screenId);
    }
  };

  const toggleLocation = (sectionScreens: Screen[]) => {
    const ids = sectionScreens.map((screen) => screen.id);
    const allOn = ids.every((id) => selectedIds.includes(id));
    if (!editing) {
      setSelectedIds((current) =>
        allOn
          ? current.filter((id) => !ids.includes(id))
          : [...current, ...ids.filter((id) => !current.includes(id))],
      );
      return;
    }
    if (allOn) {
      for (const id of ids) {
        if (selectedIds.includes(id)) {
          setSelectedIds((current) => current.filter((item) => item !== id));
          removeMember.mutate(id);
        }
      }
    } else {
      for (const id of ids) {
        if (!selectedIds.includes(id)) {
          setSelectedIds((current) => [...current, id]);
          addMember.mutate(id);
        }
      }
    }
  };

  const applyPreset = (preset: GroupScreenPreset) => {
    setSelectedIds(preset.screenIds);
    if (!name.trim()) setName(preset.name);
  };

  const commitName = () => {
    if (!editing || !group) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === group.name) {
      setName(group.name);
      return;
    }
    rename.mutate(trimmed);
  };

  if (!open) return null;

  const countLabel = selectedIds.length
    ? t('drawer.picked', { count: selectedIds.length })
    : t('drawer.nonePicked');

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[100] flex justify-end"
        style={{ background: 'rgba(10,10,11,0.35)' }}
        onClick={onClose}
      >
        <aside
          data-theme={theme}
          role="dialog"
          aria-modal="true"
          aria-labelledby="group-drawer-title"
          className="flex h-full w-[min(480px,100%)] flex-col border-s"
          style={{
            background: 'var(--gs-panel)',
            color: 'var(--gs-ink)',
            borderColor: 'var(--gs-line)',
            animation: 'gs-slide 220ms ease both',
            fontFamily: 'inherit',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="flex shrink-0 items-center justify-between gap-3 border-b px-6 py-[22px]"
            style={{ borderColor: 'var(--gs-line)' }}
          >
            <span
              id="group-drawer-title"
              className="text-[19px] font-extrabold tracking-[-0.02em]"
              style={{ color: 'var(--gs-ink)' }}
            >
              {editing ? t('drawer.editTitle') : t('drawer.newTitle')}
            </span>
            <button
              type="button"
              title={t('common:actions.close')}
              onClick={onClose}
              className="grid place-items-center p-1.5"
              style={{ color: 'var(--gs-muted)', background: 'none', border: 'none' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-[22px] overflow-y-auto px-6 py-[22px]">
            <label className="flex flex-col gap-2">
              <span className="text-[13px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
                {t('drawer.nameLabel')}
              </span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                onBlur={commitName}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                }}
                placeholder={t('drawer.namePlaceholder')}
                className="rounded-[10px] border px-3.5 py-3 text-[15px] font-semibold outline-none focus:border-[var(--gs-accent)]"
                style={{
                  color: 'var(--gs-ink)',
                  background: 'var(--gs-field)',
                  borderColor: 'var(--gs-line)',
                  fontFamily: 'inherit',
                }}
              />
            </label>

            {!editing && presets.length > 0 ? (
              <div className="flex flex-col gap-[9px]">
                <span className="text-[13px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
                  {t('drawer.startFrom')}
                </span>
                <div className="flex flex-wrap gap-[7px]">
                  {presets.map((preset) => (
                    <button
                      key={preset.name}
                      type="button"
                      disabled={busy}
                      onClick={() => applyPreset(preset)}
                      className="rounded-full border-[1.5px] px-3 py-[7px] text-[13px] font-semibold disabled:opacity-50"
                      style={{
                        color: 'var(--gs-ink2)',
                        background: 'transparent',
                        borderColor: 'var(--gs-line)',
                        fontFamily: 'inherit',
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="flex flex-col gap-3.5">
              <div className="flex items-baseline justify-between gap-2.5">
                <span className="text-[13px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
                  {t('drawer.screensInGroup')}
                </span>
                <span className="text-[13px] font-semibold" style={{ color: 'var(--gs-muted)' }}>
                  {countLabel}
                </span>
              </div>

              {sections.map((section) => {
                const allOn = section.screens.every((screen) =>
                  selectedIds.includes(screen.id),
                );
                return (
                  <div key={section.locationId ?? 'none'} className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-2.5">
                      <span
                        className="text-xs font-bold uppercase tracking-[0.12em]"
                        style={{ color: 'var(--gs-muted)' }}
                      >
                        {section.name}
                      </span>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => toggleLocation(section.screens)}
                        className="p-0.5 text-[12.5px] font-bold disabled:opacity-50"
                        style={{
                          color: 'var(--gs-accent)',
                          background: 'none',
                          border: 'none',
                          fontFamily: 'inherit',
                        }}
                      >
                        {allOn ? t('drawer.clear') : t('drawer.selectAll')}
                      </button>
                    </div>
                    {section.screens.map((screen) => {
                      const on = selectedIds.includes(screen.id);
                      const status = getCardConnectionStatus(screen);
                      const dot =
                        status === 'online'
                          ? 'var(--gs-ok)'
                          : status === 'offline'
                            ? 'var(--gs-bad)'
                            : status === 'unknown'
                              ? 'var(--gs-warn)'
                              : 'var(--gs-muted)';
                      return (
                        <button
                          key={screen.id}
                          type="button"
                          disabled={busy}
                          onClick={() => toggleScreen(screen.id)}
                          className="flex w-full items-center gap-3 rounded-[10px] border-[1.5px] px-[13px] py-[11px] text-start disabled:opacity-50"
                          style={{
                            background: on ? 'var(--gs-panel2)' : 'transparent',
                            borderColor: on ? 'var(--gs-accent)' : 'var(--gs-line)',
                            fontFamily: 'inherit',
                          }}
                        >
                          <span
                            className="grid h-5 w-5 shrink-0 place-items-center rounded-md border-[1.5px] text-xs font-extrabold text-white"
                            style={{
                              background: on ? 'var(--gs-accent-solid)' : 'transparent',
                              borderColor: on ? 'var(--gs-accent)' : 'var(--gs-line)',
                            }}
                            aria-hidden
                          >
                            {on ? '✓' : ''}
                          </span>
                          <span
                            className="min-w-0 flex-1 truncate text-start text-sm font-semibold"
                            style={{ color: 'var(--gs-ink)' }}
                          >
                            {screen.name}
                          </span>
                          <span
                            className="flex items-center gap-1.5 text-[12.5px]"
                            style={{ color: 'var(--gs-muted)' }}
                          >
                            <span
                              className="h-[7px] w-[7px] rounded-full"
                              style={{ background: dot }}
                              aria-hidden
                            />
                            {statusLabel(status)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {error ? (
              <p className="m-0 text-sm" style={{ color: 'var(--gs-bad)' }} role="alert">
                {error}
              </p>
            ) : null}
          </div>

          <div
            className="flex shrink-0 flex-wrap items-center gap-2.5 border-t px-4 py-4 sm:px-6"
            style={{ borderColor: 'var(--gs-line)' }}
          >
            {editing ? (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setConfirmDelete(true)}
                  className="px-1 py-3 text-sm font-bold disabled:opacity-50"
                  style={{
                    color: 'var(--gs-bad)',
                    background: 'none',
                    border: 'none',
                    fontFamily: 'inherit',
                  }}
                >
                  {t('drawer.deleteGroup')}
                </button>
                <span
                  className="hidden text-[12.5px] sm:ms-auto sm:inline"
                  style={{ color: 'var(--gs-muted)' }}
                >
                  {t('drawer.savesAsYouGo')}
                </span>
                <button
                  type="button"
                  onClick={onClose}
                  className="ms-auto rounded-[10px] px-[18px] py-3 text-sm font-bold sm:ms-0"
                  style={{
                    color: 'var(--gs-accent-ink)',
                    background: 'var(--gs-accent-solid)',
                    fontFamily: 'inherit',
                  }}
                >
                  {t('common:actions.done')}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-[9px] border px-4 py-3 text-[13.5px] font-bold"
                  style={{
                    color: 'var(--gs-ink)',
                    background: 'var(--gs-panel)',
                    borderColor: 'var(--gs-line)',
                    fontFamily: 'inherit',
                  }}
                >
                  {t('common:actions.cancel')}
                </button>
                <button
                  type="button"
                  disabled={!canCreate}
                  onClick={() => create.mutate()}
                  className="flex-1 rounded-[10px] px-[18px] py-3 text-sm font-bold disabled:cursor-not-allowed"
                  style={{
                    color: 'var(--gs-accent-ink)',
                    background: 'var(--gs-accent-solid)',
                    opacity: canCreate ? 1 : 0.45,
                    fontFamily: 'inherit',
                  }}
                >
                  {create.isPending ? t('drawer.creating') : t('drawer.createGroup')}
                </button>
              </>
            )}
          </div>
        </aside>
      </div>

      {confirmDelete && group ? (
        <ConfirmDialog
          title={t('drawer.confirmDeleteTitle', { name: group.name })}
          description={t('drawer.confirmDeleteDescription')}
          confirmLabel={t('drawer.deleteGroup')}
          tone="danger"
          confirming={removeGroup.isPending}
          onCancel={() => {
            if (!removeGroup.isPending) setConfirmDelete(false);
          }}
          onConfirm={() => removeGroup.mutate()}
        />
      ) : null}
    </>,
    document.body,
  );
}
